import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from './protected-route';
import { AdminRoute } from './admin-route';
import { BuilderAiRoute } from './builder-ai-route';
import { MisalignmentsRoute } from './misalignments-route';
import { PrimeBookingActivityRoute } from './prime-booking-activity-route';
import { XcelPrelicensingRoute } from './xcel-prelicensing-route';
import { HelpdeskRoute } from './helpdesk-route';
import { FeatureRequestRoute } from './feature-request-route';
import { MatchupMetricsRoute } from './matchup-metrics-route';
import { CodeOfHonorRoute } from './code-of-honor-route';
import { WbPipelineRoute } from './wb-pipeline-route';
import { ContestSettingsRoute } from './contest-settings-route';
import { GuidanceRoute } from './guidance-route';
import { GuideReaderRoute } from './guide-reader-route';
import { PluginFeesAccessRoute, PluginFeesReviewRoute } from './plugin-fees-review-route';
import {
  canManageAdjustments,
  canManageCosts,
  canSeeAgentStatements,
  canSeeCycles,
  canSeeFeeSettings,
  canSeeOverview,
  canSeeOwnStatement,
  canSeePayments,
  canSeePayouts,
  canSeeSevcTotals,
} from '@/features/plugin-fees/utils/plugin-fees-access';
import { ContestsRoute } from './contests-route';
import { LeaderboardsRoute } from './leaderboards-route';
import { ProductsRoute } from './products-route';
import { PublicRoute } from './public-route';
import { RouteErrorFallback } from './route-error-boundary';
import { RootRedirect } from './root-redirect.tsx';
import { MainLayout } from '@shared/layouts';
import { BpmSelectionProvider } from '@/features/bpm/context/bpm-selection-context';
import { BpmConfigProvider } from '@/features/bpm/context/bpm-config-context';
import { BpmSubToolBar } from '@/features/bpm/components/bpm-subtool-bar';
import { LoginPage, SignupPage } from '@/features/auth';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/features/home/pages/home-page'));
const EventsListPage = lazy(() => import('@/features/events/pages/events-list-page'));
const PurchasesPage = lazy(() => import('@/features/events/pages/purchases-page'));
const CheckinPage = lazy(() => import('@/features/events/pages/checkin-page'));
const RecognitionPage = lazy(() => import('@/features/events/pages/recognition-page'));
const PermissionsPage = lazy(() => import('@/features/events/pages/permissions-page'));
const ExternalTeamsPage = lazy(() => import('@/features/events/pages/external-teams-page'));
const ConventionTrackingPage = lazy(() => import('@/features/events/pages/convention-tracking-page'));
const EventBuilderPage = lazy(() => import('@/features/events/pages/event-builder-page'));
const EventOrdersPage = lazy(() => import('@/features/events/pages/event-orders-page'));
const EventExternalTicketsPage = lazy(() => import('@/features/events/pages/event-external-tickets-page'));
const EventMyTicketsPage = lazy(() => import('@/features/events/pages/event-my-tickets-page'));
const EventCheckinPage = lazy(() => import('@/features/events/pages/event-checkin-page'));
const EventSessionsPage = lazy(() => import('@/features/events/pages/event-sessions-page'));
const EventReviewsPage = lazy(() => import('@/features/events/pages/event-reviews-page'));
const SessionSelfCheckinPage = lazy(
  () => import('@/features/events/pages/session-self-checkin-page'),
);
const EventRecognitionPage = lazy(() => import('@/features/events/pages/event-recognition-page'));
const EventEmailsPage = lazy(() => import('@/features/events/pages/event-emails-page'));
const EventQuestionsPage = lazy(() => import('@/features/events/pages/event-questions-page'));
const EventPermissionsPage = lazy(() => import('@/features/events/pages/event-permissions-page'));
const EventLandingPage = lazy(() => import('@/features/events/pages/public/event-landing-page'));
const EventCheckoutPage = lazy(() => import('@/features/events/pages/public/event-checkout-page'));
const EventTransferPage = lazy(() => import('@/features/events/pages/public/event-transfer-page'));
const EventTicketPage = lazy(() => import('@/features/events/pages/public/event-ticket-page'));
// BPM's one public page: the pass a guest is emailed or texted a link to.
const BpmGuestPassPage = lazy(() => import('@/features/bpm/pages/public/bpm-guest-pass-page'));
// Prime Corporate Services booking: public guest page, member page, admin activity trail.
const PrimeBookingGuestPage = lazy(() => import('@/features/prime-booking/pages/prime-booking-guest-page'));
const PrimeBookingMemberPage = lazy(() => import('@/features/prime-booking/pages/prime-booking-member-page'));
const PrimeBookingActivityPage = lazy(() => import('@/features/prime-booking/pages/prime-booking-activity-page'));
const XcelPrelicensingPage = lazy(() => import('@/features/xcel-prelicensing/pages/xcel-prelicensing-page'));
const EducationPage = lazy(() => import('@/features/education/pages/education-page'));
const ProspectTrackerPage = lazy(() => import('@/features/team/prospect/pages/prospect-tracker-page'));
const OrgChartPage = lazy(() => import('@/features/team/org-chart/pages/org-chart-page'));
const MissionTrackerPage = lazy(() => import('@/features/team/mission-tracker/pages/mission-tracker-page'));
const AssociateTrackerPage = lazy(() => import('@/features/team/associate-tracker/pages/associate-tracker-page'));
const BuildersPage = lazy(() => import('@/features/team/builders/pages/builders-page'));
const ProducersPage = lazy(() => import('@/features/team/producers/pages/producers-page'));
const BuilderAiHomePage = lazy(() => import('@/features/builder-ai/pages/home-page'));
const BuilderAiCompanyPage = lazy(() => import('@/features/builder-ai/pages/company-page'));
const BuilderAiBaseshopPage = lazy(() => import('@/features/builder-ai/pages/baseshop-page'));
const BuilderAiInvitationsPage = lazy(() => import('@/features/builder-ai/pages/invitations-page'));
const BuilderAiReportingPage = lazy(() => import('@/features/builder-ai/pages/reporting-page'));
const BuilderAiBulletinPage = lazy(() => import('@/features/builder-ai/pages/bulletin-page'));
const LeaderMisalignmentsPage = lazy(
  () => import('@/features/admin/misalignments/pages/leader-misalignments-page')
);
const PolicyMisalignmentsPage = lazy(
  () => import('@/features/admin/misalignments/pages/policy-misalignments-page')
);
const LeaderboardsPage = lazy(
  () => import('@/features/leaderboards/pages/leaderboards-page')
);
const CodeOfHonorCommitteePage = lazy(
  () => import('@/features/code-of-honor/pages/code-of-honor-committee-page')
);
const ContestsPage = lazy(() => import('@/features/contests/pages/contests-page'));
const ContestSettingsPage = lazy(
  () => import('@/features/contests/pages/contest-settings-page')
);
const GuidanceAdminPage = lazy(() => import('@/features/gms/pages/guidance-admin-page'));
const GuideReaderPage = lazy(() => import('@/features/gms/pages/guide-reader-page'));
const GuideReviewPage = lazy(() => import('@/features/gms/pages/guide-review-page'));
const PluginFeesReviewPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-review-page')
);
const PluginFeesStatementPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-statement-page')
);
const PluginFeesCyclesPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-cycles-page')
);
const PluginFeesCostsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-costs-page')
);
const PluginFeesPaymentsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-payments-page')
);
const PluginFeesAgentStatementPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-agent-statement-page')
);
const PluginFeesOverviewPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-overview-page')
);
const PluginFeesPayoutsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-payouts-page')
);
const PluginFeesSevcTotalsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-sevc-totals-page')
);
const PluginFeesAdjustmentsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-adjustments-page')
);
const PluginFeesSettingsPage = lazy(
  () => import('@/features/plugin-fees/pages/plugin-fees-settings-page')
);
const WbPipelinePage = lazy(
  () => import('@/features/admin/wb-pipeline/pages/wb-pipeline-page')
);
const ProductsListPage = lazy(
  () => import('@/features/admin/products/pages/products-list-page')
);
const PublicDailySixPage = lazy(() => import('@/features/team/builders/pages/public-daily-six-page'));
const PublicDailySixHistoryPage = lazy(() => import('@/features/team/builders/pages/public-daily-six-history-page'));
const AddGoalsPage = lazy(() => import('@/features/team/associate-tracker/pages/add-goals-page'));
const LicensingTrackerPage = lazy(() => import('@/features/team/licensing-tracker/pages/licensing-tracker-page'));
const ProductionTrackerPage = lazy(() => import('@/features/team/production-tracker/pages/production-tracker-page'));
const ReportsPage = lazy(() => import('@/features/reports/pages/reports-page'));
const SettingsPage = lazy(() => import('@/features/settings/pages/settings-page'));
const ComponentsShowcase = lazy(() => import('@/features/showcase/pages/components-showcase'));
const PublicInsightCenter = lazy(() => import('@/features/insight-center/pages/public-insight-center'));
const PublicBusinessPage = lazy(() => import('@/features/education/pages/public-business-page'));
const PublicEducationPage = lazy(() => import('@/features/education/pages/public-education-page'));
const FileVaultPage = lazy(() => import('@/features/file-vault/pages/file-vault-page'));
const AdminFileVaultPage = lazy(() => import('@/features/admin/file-vault/pages/admin-file-vault-page'));
const AdminTrainingCenterPage = lazy(() => import('@/features/admin/training-center/pages/admin-training-center-page'));
const AdminSystematicToolsPage = lazy(() => import('@/features/admin/systematic-tools/pages/admin-systematic-tools-page'));
const AdminHomeContentPage = lazy(() => import('@/features/admin/home-content/pages/admin-home-content-page'));
const AdminPromotionPage = lazy(() => import('@/features/admin/promotion/pages/admin-promotion-page'));
const TrackMyLicensePage = lazy(() => import('@/features/licensing/track-my-license/pages/track-my-license-page'));
const LicensingDocumentsPage = lazy(() => import('@/features/licensing/licensing-documents/pages/licensing-documents-page'));
const CrashCoursePage = lazy(() => import('@/features/licensing/crash-course/pages/crash-course-page'));
const ChapterCoursePage = lazy(() => import('@/features/licensing/crash-course/pages/chapter-course-page'));
const TenSystematicToolsPage = lazy(() => import('@/features/systematic-tools/pages/ten-systematic-tools-page'));
const OnboardingGamePage = lazy(() => import('@/features/team/onboarding-game/pages/onboarding-game-page'));
const WelcomeVideosPage = lazy(() => import('@/features/welcome-videos/pages/welcome-videos-page'));
const ResetPasswordPage = lazy(() => import('@/features/auth/components/reset-password-page'));
// Assistant invite email link target: preview the invite and set the assistant's password.
const AssistantInviteAcceptPage = lazy(
  () => import('@/features/assistant-access/pages/assistant-invite-accept-page'),
);
const TrainingCenterPage = lazy(() => import('@/features/training-center/pages/training-center-page'));
const TrainingSchedulePage = lazy(() => import('@/features/training-schedule/pages/training-schedule-page'));
const TrainingAttendancePage = lazy(
  () => import('@/features/training-schedule/pages/training-attendance-page'),
);
const MatchupPage = lazy(() => import('@/features/matchup/pages/matchup-page'));
const CalendarPage = lazy(() => import('@/features/matchup/pages/calendar-page'));
const MatchupMetricsPage = lazy(() => import('@/features/matchup/metrics/pages/matchup-metrics-page'));
const BpmOverviewPage = lazy(() => import('@/features/bpm/pages/bpm-overview-page'));
const BpmSchedulePage = lazy(() => import('@/features/bpm/pages/bpm-schedule-page'));
const BpmAddGuestPage = lazy(() => import('@/features/bpm/pages/add-guest-page'));
const BpmViewInvitesPage = lazy(() => import('@/features/bpm/pages/view-invites-page'));
const BpmAssociateInvitesPage = lazy(() => import('@/features/bpm/pages/associate-invites-page'));
const BpmGuestCheckinPage = lazy(() => import('@/features/bpm/pages/guest-checkin-page'));
const BpmAssociateCheckinPage = lazy(() => import('@/features/bpm/pages/associate-checkin-page'));
const BpmSettingsPage = lazy(() => import('@/features/bpm/pages/bpm-settings-page'));
const HelpNeededPage = lazy(() => import('@/features/helpdesk/pages/help-needed-page'));
const PrivacyPolicyPage = lazy(() => import('@/features/legal/pages/privacy-policy-page'));
const TermsPage = lazy(() => import('@/features/legal/pages/terms-page'));
const MyTicketsPage = lazy(() => import('@/features/helpdesk/pages/my-tickets-page'));
const AdminHelpdeskPage = lazy(() => import('@/features/helpdesk/pages/admin-helpdesk-page'));
const TeamSupportPage = lazy(() => import('@/features/helpdesk/pages/team-support-page'));
const MyFeatureRequestsPage = lazy(() => import('@/features/feature-requests/pages/my-feature-requests-page'));
const FeatureBoardPage = lazy(() => import('@/features/feature-requests/pages/feature-board-page'));
const InviteAgentsPage = lazy(() => import('@/features/admin/invite-agents/pages/invite-agents-page'));
const FunctionsPage = lazy(() => import('@/features/admin/access-control/pages/functions-page'));
const UserPermissionsPage = lazy(() => import('@/features/admin/access-control/pages/user-permissions-page'));
const LevelPermissionsPage = lazy(() => import('@/features/admin/access-control/pages/level-permissions-page'));
import { AdminMissionRingProofPage } from '@/features/admin/mission-ring-proof';
const TerminatedUsersPage = lazy(() => import('@/features/terminated-users/pages/terminated-users-page'));
const PromotionDashboardPage = lazy(() => import('@/features/promotion/pages/promotion-dashboard-page'));
const TeamPromotionPage = lazy(() => import('@/features/promotion/pages/team-promotion-page'));

// Loading component
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-lg">Loading...</div>
    </div>
  );
}

// Wrap lazy components with Suspense
function lazyLoad(Component: React.LazyExoticComponent<() => JSX.Element>) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

const router = createBrowserRouter([
  // Root - Redirect to home if authenticated, otherwise to login
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <RouteErrorFallback />,
  },

  // Login page (standalone, no layout wrapper)
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorFallback />,
  },

  // Public helpdesk page (works without login)
  {
    path: '/help-needed',
    element: lazyLoad(HelpNeededPage),
    errorElement: <RouteErrorFallback />,
  },

  // Public legal pages (must render without login for Google OAuth branding review)
  {
    path: '/privacy-policy',
    element: lazyLoad(PrivacyPolicyPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/terms-and-conditions',
    element: lazyLoad(TermsPage),
    errorElement: <RouteErrorFallback />,
  },

  // Password reset/setup page (email link target)
  {
    path: '/reset-password',
    element: lazyLoad(ResetPasswordPage),
    errorElement: <RouteErrorFallback />,
  },

  // SMD assistant invite (email link target): set password, then sign in
  {
    path: '/assistant/accept',
    element: lazyLoad(AssistantInviteAcceptPage),
    errorElement: <RouteErrorFallback />,
  },

  // Signup page
  {
    path: '/signup',
    element: (
      <PublicRoute>
        <SignupPage />
      </PublicRoute>
    ),
    errorElement: <RouteErrorFallback />,
  },

  // Public Insight Center (standalone, no auth required)
  {
    path: '/public-insight-center',
    element: lazyLoad(PublicInsightCenter),
    errorElement: <RouteErrorFallback />,
  },

  // Public Education Pages (no auth required)
  {
    path: '/learn/public-business',
    element: lazyLoad(PublicBusinessPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/learn/public-education',
    element: lazyLoad(PublicEducationPage),
    errorElement: <RouteErrorFallback />,
  },

  // Public Daily Six submission page
  {
    path: '/team/builders/daily-six/:agencyCode',
    element: lazyLoad(PublicDailySixPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/team/builders/daily-six/:agencyCode/history',
    element: lazyLoad(PublicDailySixHistoryPage),
    errorElement: <RouteErrorFallback />,
  },

  // Hidden shareable page. Recipients sign in and update their own associate tracker goals.
  {
    path: '/add-goals',
    element: (
      <ProtectedRoute>
        {lazyLoad(AddGoalsPage)}
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
  },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: 'home',
        element: lazyLoad(HomePage),
      },
      {
        // Owner-only: Company Owner & Builder are separate things — a Builder only
        // gets Baseshop + Invitations, so keep them off the owner Home page.
        path: 'builder-ai/home',
        element: <BuilderAiRoute ownerOnly>{lazyLoad(BuilderAiHomePage)}</BuilderAiRoute>,
      },
      {
        // Owner-only company overview.
        path: 'builder-ai/company',
        element: <BuilderAiRoute ownerOnly>{lazyLoad(BuilderAiCompanyPage)}</BuilderAiRoute>,
      },
      {
        // Owner-only drill-in into another owner's company.
        path: 'builder-ai/company/:ownerId',
        element: <BuilderAiRoute ownerOnly>{lazyLoad(BuilderAiCompanyPage)}</BuilderAiRoute>,
      },
      {
        // Shared: both Company Owners and Builders can see their baseshop.
        path: 'builder-ai/baseshop',
        element: <BuilderAiRoute>{lazyLoad(BuilderAiBaseshopPage)}</BuilderAiRoute>,
      },
      {
        // Shared: Builders come here to accept their invitation.
        path: 'builder-ai/invitations',
        element: <BuilderAiRoute>{lazyLoad(BuilderAiInvitationsPage)}</BuilderAiRoute>,
      },
      {
        // Owner-only reporting.
        path: 'builder-ai/reporting',
        element: <BuilderAiRoute ownerOnly>{lazyLoad(BuilderAiReportingPage)}</BuilderAiRoute>,
      },
      {
        // Owner-only bulletin.
        path: 'builder-ai/bulletin',
        element: <BuilderAiRoute ownerOnly>{lazyLoad(BuilderAiBulletinPage)}</BuilderAiRoute>,
      },
      {
        // Admin-only Data Integrity report; gated per-user by misalignments:read.
        path: 'admin/data-integrity/leader-misalignments',
        element: <MisalignmentsRoute>{lazyLoad(LeaderMisalignmentsPage)}</MisalignmentsRoute>,
      },
      {
        // Admin-only Data Integrity report; gated per-user by misalignments:read.
        path: 'admin/data-integrity/policy-misalignments',
        element: <MisalignmentsRoute>{lazyLoad(PolicyMisalignmentsPage)}</MisalignmentsRoute>,
      },
      {
        path: 'admin/prime-booking-activity',
        element: <PrimeBookingActivityRoute>{lazyLoad(PrimeBookingActivityPage)}</PrimeBookingActivityRoute>,
      },
      {
        // XCEL prelicensing intake, mapping review and activity; gated per-user by
        // xcel_prelicensing:view|review|manage (my-access).
        path: 'admin/xcel-prelicensing',
        element: <XcelPrelicensingRoute>{lazyLoad(XcelPrelicensingPage)}</XcelPrelicensingRoute>,
      },
      {
        // Code of Honor committee review + admin rules/values; gated per-user by the
        // code_of_honor:* access-console permissions (my-access can_review/can_manage).
        path: 'code-of-honor/committee',
        element: <CodeOfHonorRoute>{lazyLoad(CodeOfHonorCommitteePage)}</CodeOfHonorRoute>,
      },
      {
        // Home v2 became `/home` (leaderboards decision L16); kept so old links resolve.
        path: 'home-v2',
        element: <Navigate to="/home" replace />,
      },
      {
        // Expanded leaderboard and Full Report; behind homev2:read.
        path: 'leaderboards',
        element: <LeaderboardsRoute>{lazyLoad(LeaderboardsPage)}</LeaderboardsRoute>,
      },
      {
        // The optional standalone route from docs/contests/UI.md §1: ContestsBoard, dtez's
        // layout over the card's state and rules (UI.md §2.1a). Gated on can_view_contests,
        // which reads the same homev2:read grant the leaderboards do (decision C11).
        path: 'contests',
        element: <ContestsRoute>{lazyLoad(ContestsPage)}</ContestsRoute>,
      },
      {
        // Configuring a contest is wbreporting:manage, not the homev2:read that
        // opens the card - a different gate for a different job.
        path: 'admin/contest-settings',
        element: <ContestSettingsRoute>{lazyLoad(ContestSettingsPage)}</ContestSettingsRoute>,
      },
      {
        // Guidance library and review queue, gated per-user by gms:author.
        path: 'admin/guidance',
        element: <GuidanceRoute>{lazyLoad(GuidanceAdminPage)}</GuidanceRoute>,
      },
      {
        // Review & Edit one guide (G18). Same guard as the library: gms:author.
        path: 'admin/guidance/:toolKey/:topicKey',
        element: <GuidanceRoute>{lazyLoad(GuideReviewPage)}</GuidanceRoute>,
      },
      {
        // One walkthrough as steps with screenshots, printable as a PDF. gms:read.
        path: 'guides/:toolKey/:topicKey',
        element: <GuideReaderRoute>{lazyLoad(GuideReaderPage)}</GuideReaderRoute>,
      },
      {
        // Plug-in fees admin overview (P6): plugin_fees:manage or :review.
        path: 'admin/plugin-fees',
        element: (
          <PluginFeesAccessRoute allow={canSeeOverview}>
            {lazyLoad(PluginFeesOverviewPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Hierarchy Assistant office/assistant review queues; gated per-user by
        // plugin_fees:review (my-access can_review).
        path: 'admin/plugin-fees/review',
        element: <PluginFeesReviewRoute>{lazyLoad(PluginFeesReviewPage)}</PluginFeesReviewRoute>,
      },
      {
        // The agent's own plug-in fee statement of account; an active MD or SMD
        // (my-access is_billable).
        path: 'plugin-fees/statement',
        element: (
          <PluginFeesAccessRoute allow={canSeeOwnStatement}>
            {lazyLoad(PluginFeesStatementPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Billing cycle preview / reports / approve: plugin_fees:manage, :review or
        // :payout_approve. Preview and Approve are further gated inside the page.
        path: 'admin/plugin-fees/cycles',
        element: (
          <PluginFeesAccessRoute allow={canSeeCycles}>
            {lazyLoad(PluginFeesCyclesPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Recognition and mailing costs: plugin_fees:manage.
        path: 'admin/plugin-fees/costs',
        element: (
          <PluginFeesAccessRoute allow={canManageCosts}>
            {lazyLoad(PluginFeesCostsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Payments dashboard and follow-ups (P4): plugin_fees:manage or :review. Send now
        // and Resolve are further gated (:manage) inside the page.
        path: 'admin/plugin-fees/payments',
        element: (
          <PluginFeesAccessRoute allow={canSeePayments}>
            {lazyLoad(PluginFeesPaymentsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Quarterly payouts (P5): plugin_fees:manage, :review or :payout_approve. Prepare
        // and Retry (:manage) and Approve (:payout_approve) are further gated inside.
        path: 'admin/plugin-fees/payouts',
        element: (
          <PluginFeesAccessRoute allow={canSeePayouts}>
            {lazyLoad(PluginFeesPayoutsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // SEVC totals by month (P6): plugin_fees:manage or :review.
        path: 'admin/plugin-fees/sevc-totals',
        element: (
          <PluginFeesAccessRoute allow={canSeeSevcTotals}>
            {lazyLoad(PluginFeesSevcTotalsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Manual ledger adjustments (P6): plugin_fees:manage only.
        path: 'admin/plugin-fees/adjustments',
        element: (
          <PluginFeesAccessRoute allow={canManageAdjustments}>
            {lazyLoad(PluginFeesAdjustmentsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Fee schedule, billing settings and their change history: plugin_fees:manage,
        // :review or :payout_approve read; editing is further gated (:manage) inside.
        path: 'admin/plugin-fees/settings',
        element: (
          <PluginFeesAccessRoute allow={canSeeFeeSettings}>
            {lazyLoad(PluginFeesSettingsPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Read-only lookup of any agent's statement: plugin_fees:review or :manage.
        path: 'admin/plugin-fees/agents/:id/statement',
        element: (
          <PluginFeesAccessRoute allow={canSeeAgentStatements}>
            {lazyLoad(PluginFeesAgentStatementPage)}
          </PluginFeesAccessRoute>
        ),
      },
      {
        // Admin-only reporting pipeline operations; gated per-user by
        // wbreporting:read / wbreporting:manage.
        path: 'admin/reporting-pipeline',
        element: <WbPipelineRoute>{lazyLoad(WbPipelinePage)}</WbPipelineRoute>,
      },
      {
        // Product catalog management; gated per-user by products:read.
        path: 'admin/products',
        element: <ProductsRoute>{lazyLoad(ProductsListPage)}</ProductsRoute>,
      },
      {
        path: 'insight-center',
        element: lazyLoad(PublicInsightCenter),
      },
      {
        path: 'dashboard',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'events',
        element: lazyLoad(EventsListPage),
      },
      {
        path: 'events/purchases',
        element: lazyLoad(PurchasesPage),
      },
      {
        path: 'events/check-in',
        element: lazyLoad(CheckinPage),
      },
      {
        path: 'events/recognition',
        element: lazyLoad(RecognitionPage),
      },
      {
        path: 'events/permissions',
        element: lazyLoad(PermissionsPage),
      },
      {
        path: 'events/external-teams',
        element: lazyLoad(ExternalTeamsPage),
      },
      {
        path: 'events/convention-tracking',
        element: lazyLoad(ConventionTrackingPage),
      },
      {
        path: 'events/:eventId/builder',
        element: lazyLoad(EventBuilderPage),
      },
      {
        path: 'events/:eventId/orders',
        element: lazyLoad(EventOrdersPage),
      },
      {
        path: 'events/:eventId/external-tickets',
        element: lazyLoad(EventExternalTicketsPage),
      },
      {
        path: 'events/:eventId/my-tickets',
        element: lazyLoad(EventMyTicketsPage),
      },
      {
        path: 'events/:eventId/checkin',
        element: lazyLoad(EventCheckinPage),
      },
      {
        path: 'events/:eventId/sessions',
        element: lazyLoad(EventSessionsPage),
      },
      {
        path: 'events/:eventId/reviews',
        element: lazyLoad(EventReviewsPage),
      },
      {
        path: 'events/:eventId/recognition',
        element: lazyLoad(EventRecognitionPage),
      },
      {
        path: 'events/:eventId/emails',
        element: lazyLoad(EventEmailsPage),
      },
      {
        path: 'events/:eventId/questions',
        element: lazyLoad(EventQuestionsPage),
      },
      {
        path: 'events/:eventId/access',
        element: lazyLoad(EventPermissionsPage),
      },
      {
        path: 'systematic-tools',
        element: lazyLoad(TenSystematicToolsPage),
      },
      {
        path: 'education',
        element: lazyLoad(EducationPage),
      },
      {
        path: 'learn/business',
        element: lazyLoad(PublicBusinessPage),
      },
      {
        path: 'learn/education',
        element: lazyLoad(PublicEducationPage),
      },
      {
        path: 'licensing/track',
        element: lazyLoad(TrackMyLicensePage),
      },
      {
        path: 'licensing/documents',
        element: lazyLoad(LicensingDocumentsPage),
      },
      {
        path: 'licensing/crash-course',
        element: lazyLoad(CrashCoursePage),
      },
      {
        path: 'licensing/chapter/:chapterId',
        element: lazyLoad(ChapterCoursePage),
      },
      {
        path: 'onboarding-game',
        element: lazyLoad(OnboardingGamePage),
      },
      {
        path: 'welcome-videos',
        element: lazyLoad(WelcomeVideosPage),
      },
      {
        path: 'welcome-videos/:videoKey',
        element: lazyLoad(WelcomeVideosPage),
      },
      {
        path: 'promotion/dashboard',
        element: lazyLoad(PromotionDashboardPage),
      },
      {
        path: 'promotion/team',
        element: lazyLoad(TeamPromotionPage),
      },
      {
        path: 'training-center',
        element: lazyLoad(TrainingCenterPage),
      },
      {
        path: 'training-schedule',
        element: lazyLoad(TrainingSchedulePage),
      },
      {
        path: 'matchup',
        element: lazyLoad(MatchupPage),
      },
      {
        // Gated per user by matchup_metrics:read (access console).
        path: 'matchup/metrics',
        element: <MatchupMetricsRoute>{lazyLoad(MatchupMetricsPage)}</MatchupMetricsRoute>,
      },
      {
        path: 'calendar',
        element: lazyLoad(CalendarPage),
      },
      {
        // Nested so every BPM sub-tool shares one BpmSelectionProvider: the
        // chosen BPM / date / location survives navigation between them instead
        // of each page resetting its own picker. Paths are unchanged.
        // BpmConfigProvider sits here for the same reason: the settings
        // singleton and the row-colour rules are each read by several
        // components across several pages, and one provider fetches them once
        // instead of every consumer issuing its own request.
        path: 'bpm',
        element: (
          <BpmSelectionProvider>
            <BpmConfigProvider>
              {/* Phones only: one-tap switching between the sub-tools (BPM D27). */}
              <BpmSubToolBar />
              <Outlet />
            </BpmConfigProvider>
          </BpmSelectionProvider>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/bpm/overview" replace />,
          },
          {
            path: 'overview',
            element: lazyLoad(BpmOverviewPage),
          },
          {
            path: 'schedule',
            element: lazyLoad(BpmSchedulePage),
          },
          {
            path: 'add-guest',
            element: lazyLoad(BpmAddGuestPage),
          },
          {
            path: 'view-invites',
            element: lazyLoad(BpmViewInvitesPage),
          },
          {
            // Ordered after Guest Invites, and nested here like the rest so it
            // inherits the sticky BPM/date selection.
            path: 'associate-invites',
            element: lazyLoad(BpmAssociateInvitesPage),
          },
          {
            path: 'guest-checkin',
            element: lazyLoad(BpmGuestCheckinPage),
          },
          {
            path: 'associate-checkin',
            element: lazyLoad(BpmAssociateCheckinPage),
          },
          {
            path: 'settings',
            element: lazyLoad(BpmSettingsPage),
          },
        ],
      },
      {
        path: 'file-vault',
        element: lazyLoad(FileVaultPage),
      },
      {
        path: 'team',
        element: <Navigate to="/team/prospect-tracker" replace />,
      },
      {
        path: 'team/prospect-tracker',
        element: lazyLoad(ProspectTrackerPage),
      },
      {
        path: 'team/org-chart',
        element: lazyLoad(OrgChartPage),
      },
      {
        path: 'team/mission-tracker',
        element: lazyLoad(MissionTrackerPage),
      },
      {
        path: 'team/associate-tracker',
        element: lazyLoad(AssociateTrackerPage),
      },
      {
        path: 'team/producers',
        element: lazyLoad(ProducersPage),
      },
      {
        path: 'team/builders',
        element: lazyLoad(BuildersPage),
      },
      {
        path: 'team/licensing-tracker',
        element: lazyLoad(LicensingTrackerPage),
      },
      {
        path: 'team/production-tracker',
        element: lazyLoad(ProductionTrackerPage),
      },
      {
        path: 'admin/invite-agents',
        element: lazyLoad(InviteAgentsPage),
      },
      {
        path: 'admin/mission-ring-proof',
        element: lazyLoad(AdminMissionRingProofPage),
      },
      {
        path: 'admin/functions',
        element: <AdminRoute>{lazyLoad(FunctionsPage)}</AdminRoute>,
      },
      {
        path: 'admin/user-permissions',
        element: <AdminRoute>{lazyLoad(UserPermissionsPage)}</AdminRoute>,
      },
      {
        path: 'admin/level-permissions',
        element: <AdminRoute>{lazyLoad(LevelPermissionsPage)}</AdminRoute>,
      },
      {
        path: 'admin/file-vault',
        element: <AdminRoute>{lazyLoad(AdminFileVaultPage)}</AdminRoute>,
      },
      {
        path: 'admin/training-center',
        element: <AdminRoute>{lazyLoad(AdminTrainingCenterPage)}</AdminRoute>,
      },
      {
        path: 'admin/systematic-tools',
        element: <AdminRoute>{lazyLoad(AdminSystematicToolsPage)}</AdminRoute>,
      },
      {
        path: 'admin/home-content',
        element: <AdminRoute>{lazyLoad(AdminHomeContentPage)}</AdminRoute>,
      },
      {
        // Promotion skills, videos and quizzes. The backend requires promotion:manage.
        path: 'admin/promotion',
        element: <AdminRoute>{lazyLoad(AdminPromotionPage)}</AdminRoute>,
      },
      {
        path: 'terminated-users',
        element: lazyLoad(TerminatedUsersPage),
      },
      {
        path: 'reports',
        element: lazyLoad(ReportsPage),
      },
      {
        path: 'settings',
        element: lazyLoad(SettingsPage),
      },
      {
        // Signed-in "My tickets"; the public form stays at /help-needed.
        path: 'helpdesk',
        element: lazyLoad(MyTicketsPage),
      },
      {
        // Helpdesk Admin; gated per-user by helpdesk:read (access console).
        path: 'admin/helpdesk',
        element: <HelpdeskRoute>{lazyLoad(AdminHelpdeskPage)}</HelpdeskRoute>,
      },
      {
        // Team Support (L1 queue); for active SMDs and their assistants (my-access team_queue).
        path: 'team/support',
        element: <HelpdeskRoute requires="team_queue">{lazyLoad(TeamSupportPage)}</HelpdeskRoute>,
      },
      {
        // Feature requests: my own (any signed-in user sees theirs; submitting needs
        // feature_requests:create, checked on the page and by the backend).
        path: 'feature-requests',
        element: lazyLoad(MyFeatureRequestsPage),
      },
      {
        // Developer board; gated by feature_requests:manage (access console).
        path: 'admin/feature-requests',
        element: <FeatureRequestRoute requires="can_manage">{lazyLoad(FeatureBoardPage)}</FeatureRequestRoute>,
      },
      {
        path: 'components',
        element: lazyLoad(ComponentsShowcase),
      },
    ],
  },

  // Public event pages (no auth required)
  {
    path: '/event/:shortcut',
    element: lazyLoad(EventLandingPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/event/:shortcut/checkout',
    element: lazyLoad(EventCheckoutPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/event/:shortcut/transfer',
    element: lazyLoad(EventTransferPage),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/event/ticket/:qrToken',
    element: lazyLoad(EventTicketPage),
    errorElement: <RouteErrorFallback />,
  },
  // Where a phone lands after scanning a session room's QR code. Signed-in, but
  // outside the app layout (a focused phone page); must stay in step with
  // `events.services.email.session_checkin_url`.
  {
    path: '/events/session-checkin/:token',
    element: <ProtectedRoute>{lazyLoad(SessionSelfCheckinPage)}</ProtectedRoute>,
    errorElement: <RouteErrorFallback />,
  },
  // Where the attendance link a host posts in a meeting chat lands. Signed-in, but
  // outside the app layout; anyone signed out goes through login and comes back.
  {
    path: '/training-schedule/attend/:token',
    element: <ProtectedRoute>{lazyLoad(TrainingAttendancePage)}</ProtectedRoute>,
    errorElement: <RouteErrorFallback />,
  },
  // Unauthenticated, and must stay in step with `bpm.services.qr.guest_pass_url`,
  // which builds this path into every message a guest is sent.
  {
    path: '/bpm/pass/:token',
    element: lazyLoad(BpmGuestPassPage),
    errorElement: <RouteErrorFallback />,
  },
  // Prime booking guest page: public, and deliberately not wrapped in PublicRoute — a
  // signed-in associate must be able to open a guest link too. Must stay in step with
  // the backend's PRIME_BOOKING_GUEST_PATH (default `/primecorporateservices/`).
  {
    path: '/primecorporateservices',
    element: lazyLoad(PrimeBookingGuestPage),
    errorElement: <RouteErrorFallback />,
  },
  // Prime booking member page: signed-in, outside the app layout (the approved page
  // carries its own Prime header and footer).
  {
    path: '/primecorporateservices/wealthbuilders',
    element: <ProtectedRoute>{lazyLoad(PrimeBookingMemberPage)}</ProtectedRoute>,
    errorElement: <RouteErrorFallback />,
  },

  // Catch all - 404
  {
    path: '*',
    element: <Navigate to="/" replace />,
    errorElement: <RouteErrorFallback />,
  },
]);

export { router };
export default router;
